import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { getFullName } from '@core/util/string';
import { BusinessUser, BusinessUserRole } from '@models/businesses';
import {
	Action,
	NgxsOnInit,
	Select,
	Selector,
	State,
	StateContext,
} from '@ngxs/store';
import { LlcTokensService } from '@repos/mortgages/llc/llc-tokens/llc-tokens.service';
import { AuthState } from '@state/auth';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import LogRocket from 'logrocket';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { Errored, Loaded, Loading, Revoke } from './llc.actions';
import {
	CONNECTION_STATUS,
	ConnectedUser,
	LlcStateModel,
	defaultLlcStateModel,
} from './llc.model';
import { BusinessState } from '@state/business';
import { AppState } from '@state/app/app.state';

type Ctx = StateContext<LlcStateModel>;

const loginConfig: MatDialogConfig = {
	maxWidth: '550px',
	width: '100%',
};

@State<LlcStateModel>({
	name: 'llcState',
	defaults: { ...defaultLlcStateModel },
})
@Injectable()
export class LlcState implements NgxsOnInit {
	static llcDialogConfig: MatDialogConfig = { ...loginConfig };

	static fctDialogConfig: MatDialogConfig = {
		...loginConfig,
		data: { type: 'FCT' },
	};

	@Selector([LlcState.connectedLawyerIds, BusinessUsersState.lawyerIds])
	static areAllLawyersConnected(connectedIds: string[], lawyerIds: string[]) {
		return (
			lawyerIds.length &&
			lawyerIds.every((lawyer) => connectedIds.includes(lawyer))
		);
	}

	@Selector([
		BusinessUsersState.getLoggedInUserRole,
		BusinessUsersState.isLoggedInUserAdmin,
	])
	static canLogin(role: BusinessUserRole, isAdmin: boolean): boolean {
		return role === BusinessUserRole.LAWYER || isAdmin;
	}

	@Selector([LlcState])
	static connectedIds(state: LlcStateModel): string[] {
		return state.connectedIds || [];
	}

	@Selector([LlcState.connectedIds, BusinessUsersState.getLawyers])
	static connectedLawyers(
		connectedIds: string[],
		lawyers: BusinessUser[],
	): BusinessUser[] {
		return lawyers?.filter((lawyer) => connectedIds.includes(lawyer.id));
	}

	@Selector([LlcState.connectedLawyers])
	static connectedLawyerIds(lawyers: BusinessUser[]): string[] {
		return lawyers.map((lawyer) => lawyer.id);
	}

	@Selector([LlcState.connectedIds, BusinessUsersState.getClerks])
	static connectedClerks(
		connectedIds: string[],
		clerks: BusinessUser[],
	): BusinessUser[] {
		return clerks?.filter((clerk) => connectedIds.includes(clerk.id));
	}

	@Selector([
		LlcState.connectedLawyers,
		LlcState.connectedClerks,
		BusinessUsersState.isLoggedInUserAdmin,
		AuthState.getUserId,
	])
	static connectedUsers(
		laywers: BusinessUser[],
		clerks: BusinessUser[],
		isAdmin: boolean,
		userId: string,
	): ConnectedUser[] {
		return [
			...laywers.map((lawyer) => ({
				...lawyer,
				name: getFullName([lawyer.firstName, lawyer.lastName]),
				canRevoke: isAdmin || lawyer.id === userId,
			})),
			...clerks.map((clerk) => ({
				...clerk,
				name: getFullName([clerk.firstName, clerk.lastName]),
				canRevoke: isAdmin || clerk.id === userId,
			})),
		];
	}

	@Selector([LlcState.connectedUsers])
	static connectedUserIds(users: ConnectedUser[]): string[] {
		return users.map((user) => user.id);
	}

	@Selector([LlcState.connectedLawyers, LlcState.isUserConnectedThemself])
	static connected(lawyers: BusinessUser[], themself: boolean): boolean {
		return !!(lawyers.length || themself);
	}

	@Selector([LlcState])
	static errored(state: LlcStateModel): boolean {
		return !!state?.error;
	}

	@Selector([LlcState])
	static loaded(state: LlcStateModel): boolean {
		return state?.loaded;
	}

	@Selector([LlcState, AuthState.getLoading])
	static loading(state: LlcStateModel, authLoading: boolean): boolean {
		return !!(state.loading || authLoading);
	}

	@Selector([LlcState.connectedUsers, AuthState.getUserId])
	static isUserConnectedThemself(users: ConnectedUser[], authId): boolean {
		return !!users.find((user) => user.id === authId);
	}

	@Selector([BusinessUsersState.isLoggedInUserAdmin, AppState.loadedFromLeap])
	static showInviteOnLoginPage(
		isAdmin: boolean,
		loadedFromLeap: boolean,
	): boolean {
		return !!isAdmin && !loadedFromLeap;
	}

	@Selector([LlcState.connected, LlcState.loading])
	static status(connected: boolean, loading: boolean): CONNECTION_STATUS {
		if (loading) {
			return CONNECTION_STATUS.CONNECTING;
		} else if (connected) {
			return CONNECTION_STATUS.CONNECTED;
		}

		return CONNECTION_STATUS.DISCONNECTED;
	}

	@Select(BusinessState.businessId) businessId$!: Observable<string>;

	constructor(
		private dialog: MatDialog,
		private llcTokens: LlcTokensService,
	) {}

	ngxsOnInit({ dispatch }: Ctx): void {
		this.businessId$
			.pipe(
				distinctUntilChanged(),
				switchMap((id) => {
					dispatch(new Loading());
					return id ? this.llcTokens.All() : of([]);
				}),
				map((data) => data.map((token) => token.id)),
			)
			.subscribe(
				(connectedIds) => dispatch(new Loaded(connectedIds)),
				(err) => dispatch(new Errored(err)),
			);
	}

	@Action(Errored)
	errored({ patchState }: Ctx, { logData }: Errored): void {
		const msg = 'Error fetching LLC connection status';
		console.warn(msg, logData);
		LogRocket.error(msg, logData);
		patchState({
			error: true,
			loading: false,
			loaded: false,
		});
	}

	@Action(Loaded)
	loaded({ patchState }: Ctx, { connectedIds }: Loaded): void {
		const loaded = !!connectedIds.length;
		patchState({
			error: false,
			loaded,
			loading: false,
			connectedIds,
		});
	}

	@Action(Loading)
	loading({ patchState }: Ctx): void {
		patchState({ loading: true, error: false });
	}

	@Action(Revoke)
	revoke(ctx: Ctx, { userId }: Revoke): void {
		this.llcTokens.Delete(userId, {});
	}
}
